import {
  callApiGet,
  callApiPatch,
  callApiPost,
  constructApiUrl,
  createParams,
  validateParams,
  getChangedParams,
  getPolicyID,
  getRequestPreparePurchase,
} from './common';
import { createErrorBody } from '../error';
import { createHouse, updateHouse, prepareHouse } from '../../actions/Api';

const type = 'house';

/**
 * Determines if the state allows us to run updates.
 * @param  {object} state The current state.
 * @return {boolean}      If we can update or not.
 */
const shouldUpdate = (state) => {
  return state.isUpdatingHouse ? state.isUpdatingHouse : true;
};

/**
 * Call the API using GET.
 * @param  {string}   service  The service to call.
 * @param  {Object}   params   The params for the service.
 * @param  {Function} dispatch The dispatch function.
 * @param  {boolean} externalService
 * @param  {boolean} useToken
 * @return {Promise}           The Promise.
 */
const callHouseApiGet = (service, params, dispatch, externalService = false, useToken = true) => {
  const url = constructApiUrl(type, service);
  return callApiGet(url, params, dispatch, externalService, useToken);
};

/**
 * Call the API using POST.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
const callHouseApiPost = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPost(url, params, dispatch);
};

/**
 * Call the API using PATCH.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @param  {function} dispatch
 * @return {Promise}        The Promise.
 */
const callHouseApiPatch = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPatch(url, params, dispatch);
};

/**
 * Get excess amount for House.
 *
 * @param {Object} formValues
 * @param {function} dispatch
 * @return {Promise}
 */
export const getHouseExcessAmount = (formValues, dispatch) => {
  const { user: { customerID } } = formValues;
  const policyID = getPolicyID(formValues, type, true);
  const service = `getExcessAmount/${customerID}/${policyID}`;
  return callHouseApiGet(service, {}, dispatch, false, false);
};

/**
 * Dispatches the create action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callCreateHouse = (service, params) => {
  return dispatch => {
    dispatch(createHouse());
    return callHouseApiPost(service, params, dispatch);
  };
};

/**
 * Call the create  service.
 * @param  {Object} params The params to call the service with.
 * @param  {Object} formValues
 * @return {Promise}     The promise of the service.
 */
export const requestCreateHouse = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const mappedParams = params ? createParams(params, formValues, getState()) : {};
      // Ensure that we have the correct paramters. Fail if not.
      const errors = params ? validateParams(mappedParams, params) : [];
      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }
      const service = '';
      return dispatch(callCreateHouse(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the update action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callUpdateHouse = (service, params) => {
  return dispatch => {
    dispatch(updateHouse());
    return callHouseApiPatch(service, params, dispatch);
  };
};

/**
 * Call the update service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}           The promise of the service.
 */
export const requestUpdateHouse = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      let mappedParams = params ? createParams(params, formValues, state) : {};
      const policyID = getPolicyID(state.userData, type);
      if (state.userData.house) {
        // Find the changed values and pass those along.
        const additionalParams = { policyID };
        mappedParams = getChangedParams(mappedParams, state.userData.house.usedParams, ['policyID'], additionalParams);
      }
      const service = `${policyID}`;
      return dispatch(callUpdateHouse(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the prepare action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPrepareHouse = (service, params) => {
  return dispatch => {
    dispatch(prepareHouse());
    return callHouseApiPatch(service, params, dispatch);
  };
};

/**
 * Call the prepare purchase service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}     The promise of the service.
 */
export const requestPrepareHouse = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const prepare = getRequestPreparePurchase(type, getState(), formValues, params);
      return dispatch(callPrepareHouse(prepare.service, prepare.mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};



// WEBPACK FOOTER //
// ./src/utilities/api/house.js